import type { ComponentProps } from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils.js';

/* A segmented control drawn as one rule-lined strip with hairline dividers,
   not a row of separate buttons. */
export const toggleVariants = cva(
  cn(
    'inline-flex flex-1 items-center justify-center gap-(--space-2) whitespace-nowrap',
    'text-[length:var(--text-small)] font-medium text-ink-muted',
    'transition-[background-color,color] duration-(--duration-fast) ease-(--ease-out-expo)',
    'hover:bg-surface-hover hover:text-ink',
    /*
     * Selected is expressed twice, and both are load-bearing.
     *
     * `data-state` is Radix's own, and it is the one that disappears: an item
     * used as a `TooltipTrigger asChild` shares its DOM node with the trigger,
     * which writes its own `data-state` (open/closed) over the item's on/off —
     * so a tooltip-wrapped toggle would silently never look selected.
     * `aria-checked` (single) and `aria-pressed` (multiple) survive that,
     * because nothing else writes them, and they are what assistive tech reads
     * anyway — which makes the tint and the announcement the same fact.
     */
    'data-[state=on]:bg-accent-quiet data-[state=on]:text-accent',
    'aria-checked:bg-accent-quiet aria-checked:text-accent',
    'aria-pressed:bg-accent-quiet aria-pressed:text-accent',
    'disabled:pointer-events-none disabled:opacity-70',
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
    'border-l border-rule first:border-l-0',
  ),
  {
    variants: {
      size: {
        default: 'px-(--space-3) py-(--space-2)',
        sm: 'px-(--space-2) py-(--space-1)',
        icon: 'size-8 p-0',
      },
    },
    defaultVariants: { size: 'default' },
  },
);

export function ToggleGroup({
  className,
  ...props
}: ComponentProps<typeof ToggleGroupPrimitive.Root>) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      className={cn(
        /* The height lives on the root and the items stretch into it, so a
           segmented control is exactly as tall as the button beside it — the
           items cannot each carry the height, because the root's border would
           then make the whole strip two pixels taller than everything else. */
        'inline-flex h-(--control-height) w-fit items-stretch overflow-hidden rounded-(--radius-md) border border-rule-strong bg-card',
        className,
      )}
      {...props}
    />
  );
}

export function ToggleGroupItem({
  className,
  size,
  ...props
}: ComponentProps<typeof ToggleGroupPrimitive.Item> & VariantProps<typeof toggleVariants>) {
  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      className={cn(toggleVariants({ size }), className)}
      {...props}
    />
  );
}
